import { useLazyCheckNameQuery } from "@/modules/template/services";
import {
  createTemplateNameValidationSchema
} from "@/modules/template/validations/validationSchema";
import { Notification } from "@/shared/components";
import { useNotify } from "@/shared/hooks";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import type { TFormData } from "@ab.uitools/ui-library/types/globalTypes";
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react";
import type { UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { TemplateNameForm } from "./TemplateNameForm";

interface Props {
  isOpen: boolean;
  isRename?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  templateName?: string;
  setTemplateName?: Dispatch<SetStateAction<string>>;
  onConfirm: (name: string) => void;
  buttonText?: string;
}

export const TemplateNameModal = ({
  isOpen,
  onClose,
  isRename,
  isLoading,
  onConfirm,
  templateName,
  setTemplateName,
  buttonText,
}: Props) => {
  const { t } = useTranslation();
  const { showErrorAlertNotification } = useNotify();
  const [checkName, { isFetching }] = useLazyCheckNameQuery();
  const setFieldError = useRef<UseFormSetError<TFormData> | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const handleSubmit = async (data: TFormData, isDirty: boolean) => {
    checkName({ name: data.templateName })
      .unwrap()
      .then((isUniqueName) => {
        if (!isUniqueName) {
          setFieldError.current?.("templateName", {
            type: "manual",
            message: t("template.templateNameModal.templateNameNotUnique"),
          });
          return;
        }
        if (isDirty) {
          setTemplateName?.(data.templateName);
          onConfirm(data.templateName);
        }
      })
      .catch((err) => showErrorAlertNotification(err));
  };

  const onInit = (fn: UseFormSetError<TFormData>) => {
    setFieldError.current = fn;
  };

  useEffect(() => {
    if (!isOpen) {
      setTemplateName?.("");
    }
  }, [isOpen]);

  return (
    <Modal
      closeIcon
      titleProps={{
        title: t("template.templateNameModal.title"),
        size: "large"
      }}
      subtitle={t("template.templateNameModal.subtitle")}
      closeOnOutsideClick={false}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => {}}
      buttonProps={{
        confirm: {
          formId: "template-name-form",
          isLoading: isFetching || isLoading,
          buttonText: t(buttonText || "general.continue"),
          buttonActionType: "submit",
          disabled: isRename && !hasChanges,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
    >
      <Notification className="mb-8"/>
      <FormContainer
        mode="onBlur"
        reValidateMode="onBlur"
        className="full-width"
        formId="template-name-form"
        initialValues={{ templateName }}
        onSubmit={(data, formState) => handleSubmit(data, formState.isDirty)}
        validationScheme={createTemplateNameValidationSchema({ t })}
      >
        <TemplateNameForm
          defaultValue={templateName}
          setHasChanges={setHasChanges}
          isRename={isRename}
          onInit={onInit}
        />
      </FormContainer>
    </Modal>
  );
};
